import React, { useEffect, useState, useMemo } from 'react';
import Icon from '../components/Icon';
import API from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import PageHeader from '../components/PageHeader';
import './TimetableBuilder.css';

const DAYS = ['B.e', 'Ç.a', 'Çər', 'C.a', 'Cümə'];
const DAY_FULL = ['Bazar ertəsi', 'Çərşənbə axşamı', 'Çərşənbə', 'Cümə axşamı', 'Cümə'];
const LESSONS = [1, 2, 3, 4, 5, 6, 7, 8];

export default function TimetableBuilder() {
  const { t } = useLanguage();
  const TABS = [
    { key: 'requirements', label: t('timetable.requirements') },
    { key: 'availability', label: t('timetable.availability') },
    { key: 'rooms', label: t('timetable.rooms') },
    { key: 'generate', label: t('timetable.autoGenerate') },
    { key: 'view', label: t('timetable.scheduleView') },
    { key: 'master', label: t('timetable.masterGrid') },
  ];
  const [tab, setTab] = useState('requirements');
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [groups, setGroups] = useState([]);

  const loadAll = async () => {
    const [c, s, t, rr, req, cf, gr] = await Promise.all([
      API.get('/classes'),
      API.get('/subjects'),
      API.get('/teachers'),
      API.get('/timetable/rooms'),
      API.get('/timetable/requirements'),
      API.get('/timetable/conflicts'),
      API.get('/timetable/groups'),
    ]);
    // Exclude preschool — kindergarten doesn't have lessons/timetable
    const nonPreschool = (c.data || []).filter(cl => cl.section !== 'preschool');
    setClasses(nonPreschool); setSubjects(s.data); setTeachers(t.data);
    setRooms(rr.data); setReqs(req.data); setConflicts(cf.data); setGroups(gr.data);
  };

  useEffect(() => { loadAll(); }, []);

  return (
    <div className="ttb">
      <PageHeader icon="timetable" title={t('timetable.title')} />
      <div className="ttb-tabs">
        {TABS.map(tab2 => (
          <button
            key={tab2.key}
            className={`ttb-tab ${tab === tab2.key ? 'active' : ''}`}
            onClick={() => setTab(tab2.key)}
          >{tab2.label}</button>
        ))}
        {conflicts.length > 0 && (
          <span className="ttb-conflict-badge"><Icon name="warning" size={15} /> {conflicts.length} ziddiyyət</span>
        )}
      </div>

      <div className="ttb-body">
        {tab === 'requirements' && (
          <RequirementsTab
            classes={classes} subjects={subjects} teachers={teachers} rooms={rooms}
            reqs={reqs} groups={groups} reload={loadAll} />
        )}
        {tab === 'availability' && (
          <AvailabilityTab teachers={teachers} />
        )}
        {tab === 'rooms' && (
          <RoomsTab rooms={rooms} reload={loadAll} />
        )}
        {tab === 'generate' && (
          <GenerateTab classes={classes} reqs={reqs} reload={loadAll} />
        )}
        {tab === 'view' && (
          <ViewTab classes={classes} teachers={teachers} rooms={rooms} conflicts={conflicts} />
        )}
        {tab === 'master' && (
          <MasterGridTab classes={classes} subjects={subjects} teachers={teachers} rooms={rooms} conflicts={conflicts} reload={loadAll} />
        )}
      </div>
    </div>
  );
}

// ---------------- Requirements ----------------
function RequirementsTab({ classes, subjects, teachers, rooms, reqs, groups, reload }) {
  const { t, lang } = useLanguage();
  // groupSel is a unified dropdown value: 'whole' | 'half_a' | 'half_b' | `g-{id}`
  const EMPTY = { class_id: '', subject_id: '', teacher_id: '', hours_per_week: 1, days_per_week: 3, room_hint: '', groupSel: 'whole' };
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [divisionsModal, setDivisionsModal] = useState(null); // class_id being edited

  const openAdd = () => {
    setForm(EMPTY); setEditingId(null); setModalOpen(true);
  };

  const save = async () => {
    if (!form.class_id || !form.subject_id) return alert('Sinif və fənn seçin');
    // Split groupSel back into class_type / group_id for the backend
    const payload = { ...form };
    if (String(form.groupSel).startsWith('g-')) {
      payload.group_id = +String(form.groupSel).slice(2);
      payload.class_type = 'whole';
    } else {
      payload.group_id = null;
      payload.class_type = form.groupSel || 'whole';
    }
    delete payload.groupSel;
    try {
      if (editingId) {
        await API.put('/timetable/requirements/' + editingId, payload);
      } else {
        await API.post('/timetable/requirements', payload);
      }
      // Rebuild this class's schedule from all its requirements so the new/edited
      // requirement is reflected in the master grid immediately.
      await API.post('/timetable/generate', {
        class_ids: [+form.class_id],
        clear_existing: true,
      });
    } catch (e) {
      alert('Xəta: ' + (e.response?.data?.message || e.message));
      return;
    }
    setForm(EMPTY); setEditingId(null); setModalOpen(false);
    reload();
  };

  const startEdit = (r) => {
    setForm({
      class_id: String(r.class_id),
      subject_id: String(r.subject_id),
      teacher_id: r.teacher_id ? String(r.teacher_id) : '',
      hours_per_week: r.hours_per_week || 1,
      days_per_week: r.days_per_week || 1,
      room_hint: r.room_hint || '',
      groupSel: r.group_id ? `g-${r.group_id}` : (r.class_type || 'whole'),
    });
    setEditingId(r.id);
    setModalOpen(true);
  };

  const cancelEdit = () => { setForm(EMPTY); setEditingId(null); setModalOpen(false); };

  const remove = async (id) => {
    if (!window.confirm('Silinsin?')) return;
    const row = reqs.find(r => r.id === id);
    await API.delete('/timetable/requirements/' + id);
    if (row?.class_id) {
      try {
        await API.post('/timetable/generate', {
          class_ids: [row.class_id],
          clear_existing: true,
        });
      } catch (e) {}
    }
    if (editingId === id) cancelEdit();
    reload();
  };

  const sortedClasses = useMemo(
    () => [...classes].sort((a, b) => (a.grade_level - b.grade_level) || a.name.localeCompare(b.name)),
    [classes]);

  return (
    <div className="ttb-section">
      <div className="ttb-req-toolbar">
        <button className="btn btn-primary ttb-add-btn" onClick={openAdd}>
          <span style={{fontSize:18}}>+</span> {t('timetable.addLessonTitle')}
        </button>
      </div>

      <table className="ttb-table">
        <thead>
          <tr><th>{t('timetable.class')}</th><th>{t('timetable.group')}</th><th>{t('timetable.subject')}</th><th>{t('timetable.teacher')}</th><th>{t('timetable.hours')}</th><th>{t('timetable.days')}</th><th>{t('timetable.total')}</th><th>{t('timetable.room')}</th><th></th></tr>
        </thead>
        <tbody>
          {reqs.map(r => {
            const g = r.group_id ? groups.find(x => x.id === r.group_id) : null;
            const label = g ? g.name
              : r.class_type === 'half_a' ? t('timetable.halfA2')
              : r.class_type === 'half_b' ? t('timetable.halfB2')
              : t('timetable.whole');
            return (
            <tr key={r.id}>
              <td>{r.class_name}</td>
              <td>{label}</td>
              <td>{lang === 'en' ? t('subjectNames.' + r.subject_name, r.subject_name) : r.subject_name}</td>
              <td>{r.teacher_name || '—'}</td>
              <td>{r.hours_per_week} {t('timetable.hoursPerLesson')}</td>
              <td>{r.days_per_week || 1} {t('timetable.daysSuffix')}</td>
              <td><b>{(r.hours_per_week || 1) * (r.days_per_week || 1)} {t('timetable.hoursPerLesson')}</b></td>
              <td>{r.room_hint || '—'}</td>
              <td style={{whiteSpace:'nowrap'}}>
                <button className="ttb-edit" onClick={() => startEdit(r)} title="Redaktə et"><Icon name="edit" size={14} /></button>
                <button className="ttb-del" onClick={() => remove(r.id)} title="Sil"><Icon name="close" size={13} /></button>
              </td>
            </tr>
            );
          })}
          {!reqs.length && <tr><td colSpan="9" className="ttb-empty">{t('timetable.noRequirements')}</td></tr>}
        </tbody>
      </table>

      {divisionsModal && (
        <DivisionsModal
          classId={divisionsModal}
          className={sortedClasses.find(c => String(c.id) === String(divisionsModal))?.name || ''}
          onClose={() => { setDivisionsModal(null); reload(); }} />
      )}

      {modalOpen && (
        <div className="ttb-modal-bg" onClick={cancelEdit}>
          <div className="ttb-modal asc" onClick={e => e.stopPropagation()}>
            <div className="ttb-modal-head asc">
              <span>{editingId ? 'Tələbi redaktə et' : 'Yeni tələb'}</span>
              <button className="ttb-modal-x" onClick={cancelEdit}><Icon name="close" size={16} /></button>
            </div>
            <div className="ttb-modal-body asc">
              <div className="asc-row">
                <div className="asc-icon"><Icon name="users" size={20} /></div>
                <div className="asc-field">
                  <label>Sinif *</label>
                  <select value={form.class_id} onChange={e => setForm({ ...form, class_id: e.target.value, groupSel: 'whole' })}>
                    <option value="">— Seçin —</option>
                    {sortedClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="asc-field narrow">
                  <label>Qrup / Tip</label>
                  <select value={form.groupSel} onChange={e => setForm({ ...form, groupSel: e.target.value })}>
                    <option value="whole">Tam sinif</option>
                    <option value="half_a">Yarım sinif (A)</option>
                    <option value="half_b">Yarım sinif (B)</option>
                    {groups.filter(g => String(g.class_id) === String(form.class_id)).map(g => (
                      <option key={g.id} value={`g-${g.id}`}>
                        {g.division !== 'default' ? `${g.division}: ` : ''}{g.name}
                        {g.student_count ? ` (${g.student_count})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {form.class_id && (
                <div className="asc-row">
                  <div className="asc-icon"><Icon name="settings" size={20} /></div>
                  <div className="asc-field">
                    <button
                      type="button"
                      className="btn ttb-div-big-btn"
                      onClick={() => setDivisionsModal(form.class_id)}>
                      Bölmələri idarə et →
                    </button>
                  </div>
                </div>
              )}
              <div className="asc-row">
                <div className="asc-icon"><Icon name="book" size={20} /></div>
                <div className="asc-field">
                  <label>Fənn *</label>
                  <select value={form.subject_id} onChange={e => setForm({ ...form, subject_id: e.target.value })}>
                    <option value="">— Seçin —</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{lang === 'en' ? t('subjectNames.' + s.name, s.name) : s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="asc-row">
                <div className="asc-icon"><Icon name="graduation" size={20} /></div>
                <div className="asc-field">
                  <label>Müəllim</label>
                  <select value={form.teacher_id} onChange={e => setForm({ ...form, teacher_id: e.target.value })}>
                    <option value="">— Seçin —</option>
                    {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="asc-row">
                <div className="asc-icon"><Icon name="calendar" size={20} /></div>
                <div className="asc-field">
                  <label>Saat (bir sessiyada)</label>
                  <input type="number" min="1" max="8" value={form.hours_per_week}
                    onChange={e => setForm({ ...form, hours_per_week: +e.target.value })} />
                </div>
                <div className="asc-field">
                  <label>Gün (həftədə)</label>
                  <input type="number" min="1" max="5" value={form.days_per_week}
                    onChange={e => setForm({ ...form, days_per_week: +e.target.value })} />
                </div>
              </div>
              <div className="asc-row">
                <div className="asc-icon"><Icon name="door" size={20} /></div>
                <div className="asc-field">
                  <label>Otaq</label>
                  <select value={form.room_hint} onChange={e => setForm({ ...form, room_hint: e.target.value })}>
                    <option value="">— Seçin —</option>
                    {rooms.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="asc-slot-info">
                Cəmi: <b>{(form.hours_per_week || 1) * (form.days_per_week || 1)} saat / həftə</b>
              </div>
            </div>
            <div className="ttb-modal-foot asc">
              <span className="asc-help" title="Saat = bir dərsin uzunluğu, Gün = həftədə neçə fərqli gün">?</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={save}>{editingId ? 'Yadda saxla' : 'Əlavə et'}</button>
                <button className="btn" onClick={cancelEdit}>İmtina</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Availability ----------------
function AvailabilityTab({ teachers }) {
  const { t } = useLanguage();
  const [teacherId, setTeacherId] = useState('');
  const [grid, setGrid] = useState({});

  useEffect(() => {
    if (!teacherId) return;
    API.get('/timetable/availability?teacher_id=' + teacherId).then(r => {
      const m = {};
      r.data.forEach(a => { m[`${a.day_of_week}-${a.lesson_number}`] = a.is_available; });
      setGrid(m);
    });
  }, [teacherId]);

  const toggle = async (d, l) => {
    const key = `${d}-${l}`;
    const curr = grid[key] ?? 1;
    const next = curr ? 0 : 1;
    setGrid({ ...grid, [key]: next });
    await API.post('/timetable/availability', {
      teacher_id: teacherId, day_of_week: d, lesson_number: l, is_available: next,
    });
  };

  return (
    <div className="ttb-section">
      <div className="ttb-toolbar">
        <select value={teacherId} onChange={e => setTeacherId(e.target.value)}>
          <option value="">{t('timetable.selectTeacher')}</option>
          {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <span className="ttb-hint">{t('timetable.teacherAvailHint')}</span>
      </div>
      {teacherId && (
        <table className="ttb-grid">
          <thead>
            <tr><th>Dərs</th>{DAYS.map(d => <th key={d}>{d}</th>)}</tr>
          </thead>
          <tbody>
            {LESSONS.map(l => (
              <tr key={l}>
                <td className="ttb-ln">{l}</td>
                {[1, 2, 3, 4, 5].map(d => {
                  const v = grid[`${d}-${l}`] ?? 1;
                  return (
                    <td key={d}
                      className={`ttb-cell ${v ? 'ok' : 'blocked'}`}
                      onClick={() => toggle(d, l)}>
                      {v ? <Icon name="check" size={14} /> : <Icon name="close" size={14} />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------- Rooms ----------------
function RoomsTab({ rooms, reload }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', kind: '', capacity: '' });
  const save = async () => {
    if (!form.name) return;
    await API.post('/timetable/rooms', form);
    setForm({ name: '', kind: '', capacity: '' });
    reload();
  };
  const remove = async id => {
    if (!window.confirm('Silinsin?')) return;
    await API.delete('/timetable/rooms/' + id); reload();
  };

  return (
    <div className="ttb-section">
      <div className="ttb-form">
        <input placeholder={t('timetable.roomNamePlaceholder')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        <input placeholder={t('timetable.roomKindPlaceholder')} value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} />
        <input placeholder={t('timetable.roomCapacityPlaceholder')} type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} />
        <button className="btn btn-primary" onClick={save}>{t('timetable.btnAdd')}</button>
      </div>
      <table className="ttb-table">
        <thead><tr><th>{t('timetable.colName')}</th><th>{t('timetable.colKind')}</th><th>{t('timetable.colCapacity')}</th><th></th></tr></thead>
        <tbody>
          {rooms.map(r => (
            <tr key={r.id}>
              <td>{r.name}</td><td>{r.kind || '—'}</td><td>{r.capacity || '—'}</td>
              <td><button className="ttb-del" onClick={() => remove(r.id)}><Icon name="close" size={13} /></button></td>
            </tr>
          ))}
          {!rooms.length && <tr><td colSpan="4" className="ttb-empty">{t('timetable.noRooms')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ---------------- Generate ----------------
function GenerateTab({ classes, reqs, reload }) {
  const { t } = useLanguage();
  const [selected, setSelected] = useState([]);
  const [clearOld, setClearOld] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = id => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAll = () => setSelected(classes.map(c => c.id));
  const clearSel = () => setSelected([]);

  const run = async () => {
    if (!selected.length) return alert('Ən azı bir sinif seçin');
    setRunning(true); setResult(null);
    try {
      const r = await API.post('/timetable/generate', {
        class_ids: selected, clear_existing: clearOld,
      });
      setResult(r.data);
      reload();
    } catch (e) {
      alert('Xəta: ' + (e.response?.data?.message || e.message));
    } finally { setRunning(false); }
  };

  return (
    <div className="ttb-section">
      <div className="ttb-gen-toolbar">
        <button className="btn" onClick={selectAll}>{t('timetable.selectAll')}</button>
        <button className="btn" onClick={clearSel}>{t('timetable.clearAll')}</button>
        <label className="ttb-check">
          <input type="checkbox" checked={clearOld} onChange={e => setClearOld(e.target.checked)} />
          {t('timetable.clearExisting')}
        </label>
        <button className="btn btn-primary" onClick={run} disabled={running}>
          {running ? t('timetable.generateRunning') : <><Icon name="zap" size={14} /> {t('timetable.generate')}</>}
        </button>
      </div>
      <div className="ttb-class-grid">
        {classes.map(c => {
          const count = reqs.filter(r => r.class_id === c.id).reduce((s, r) => s + (r.hours_per_week || 1) * (r.days_per_week || 1), 0);
          return (
            <label key={c.id} className={`ttb-class-card ${selected.includes(c.id) ? 'sel' : ''}`}>
              <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
              <div>
                <div className="ttb-cc-name">{c.name}</div>
                <div className="ttb-cc-meta">{count} {t('timetable.hoursPerWeek')}</div>
              </div>
            </label>
          );
        })}
      </div>
      {result && (
        <div className="ttb-result">
          <div><b>{result.placed}</b> {t('timetable.placed')}. <b style={{ color: result.failed ? '#dc2626' : '#16a34a' }}>{result.failed}</b> {t('timetable.failed')}.</div>
          {result.failed_items?.length > 0 && (
            <ul className="ttb-failed">
              {result.failed_items.slice(0, 20).map((f, i) => (
                <li key={i}>Sinif #{f.class_id} — {f.subject_name}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------- View (with drag & drop + PDF) ----------------
function ViewTab({ classes, teachers, rooms, conflicts }) {
  const { t, lang } = useLanguage();
  const [mode, setMode] = useState('class');
  const [targetId, setTargetId] = useState('');
  const [data, setData] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [hint, setHint] = useState('');

  const load = async () => {
    if (!targetId) { setData([]); return; }
    const r = await API.get(`/timetable/view?mode=${mode}&id=${encodeURIComponent(targetId)}`);
    setData(r.data);
  };

  useEffect(() => { load(); }, [mode, targetId]);

  const options = mode === 'class' ? classes
    : mode === 'teacher' ? teachers
      : rooms.map(r => ({ id: r.name, name: r.name }));

  const conflictIds = useMemo(() => {
    const s = new Set();
    conflicts.forEach(c => c.ids.forEach(i => s.add(i)));
    return s;
  }, [conflicts]);

  const cell = (d, l) => data.find(x => x.day_of_week === d && x.lesson_number === l);

  const onDragStart = (e, c) => { setDragging(c); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = async (e, d, l) => {
    e.preventDefault();
    if (!dragging) return;
    if (dragging.day_of_week === d && dragging.lesson_number === l) { setDragging(null); return; }
    try {
      const r = await API.put('/timetable/move', { id: dragging.id, day_of_week: d, lesson_number: l });
      setHint(r.data.swapped ? 'Yerləri dəyişdirildi' : 'Köçürüldü');
      setTimeout(() => setHint(''), 2000);
      load();
    } catch (err) {
      setHint(err.response?.data?.message || 'Köçürmə alınmadı');
      setTimeout(() => setHint(''), 3000);
    } finally { setDragging(null); }
  };

  const exportPDF = () => {
    const label = options.find(o => String(o.id) === String(targetId))?.name || '';
    const title = `Cədvəl — ${mode === 'class' ? 'Sinif' : mode === 'teacher' ? 'Müəllim' : 'Otaq'}: ${label}`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px}
        h2{margin:0 0 16px}
        table{border-collapse:collapse;width:100%}
        th,td{border:1px solid #333;padding:8px;text-align:center;font-size:12px;vertical-align:top;height:70px}
        th{background:#f1f5f9}
        .s{font-weight:bold}
        .sub{color:#555;font-size:11px}
      </style></head><body>
      <h2>${title}</h2>
      <table><thead><tr><th>Dərs</th>${DAY_FULL.map(d => `<th>${d}</th>`).join('')}</tr></thead><tbody>
      ${LESSONS.map(l => `<tr><td><b>${l}</b></td>${[1, 2, 3, 4, 5].map(d => {
        const c = cell(d, l);
        if (!c) return '<td></td>';
        return `<td><div class="s">${escapeHtml(c.subject_name)}</div>
          ${mode !== 'class' ? `<div class="sub">${escapeHtml(c.class_name)}</div>` : ''}
          ${mode !== 'teacher' ? `<div class="sub">${escapeHtml(c.teacher_name || '')}</div>` : ''}
          ${c.room && mode !== 'room' ? `<div class="sub">Otaq: ${escapeHtml(c.room)}</div>` : ''}
        </td>`;
      }).join('')}</tr>`).join('')}
      </tbody></table>
      <script>window.onload=()=>{setTimeout(()=>window.print(),300)}<\/script>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="ttb-section">
      <div className="ttb-toolbar">
        <div className="ttb-mode">
          {['class', 'teacher', 'room'].map(m => (
            <button key={m} className={mode === m ? 'on' : ''} onClick={() => { setMode(m); setTargetId(''); }}>
              {m === 'class' ? t('timetable.class') : m === 'teacher' ? t('timetable.teacher') : t('timetable.room')}
            </button>
          ))}
        </div>
        <select value={targetId} onChange={e => setTargetId(e.target.value)}>
          <option value="">{t('timetable.selectDash')}</option>
          {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <button className="btn" onClick={() => window.print()}><Icon name="printer" size={14} /> {t('timetable.btnPrint')}</button>
        <button className="btn" onClick={exportPDF} disabled={!targetId}><Icon name="file" size={14} /> PDF</button>
        {hint && <span className="ttb-hint-msg">{hint}</span>}
        {mode === 'class' && <span className="ttb-hint">{t('timetable.dragLessons')}</span>}
      </div>

      {targetId && (
        <table className="ttb-grid ttb-view">
          <thead>
            <tr><th>Dərs</th>{DAY_FULL.map((d, i) => <th key={i}>{d}</th>)}</tr>
          </thead>
          <tbody>
            {LESSONS.map(l => (
              <tr key={l}>
                <td className="ttb-ln">{l}</td>
                {[1, 2, 3, 4, 5].map(d => {
                  const c = cell(d, l);
                  const bad = c && conflictIds.has(c.id);
                  const canDrag = mode === 'class';
                  return (
                    <td key={d}
                      className={`ttb-vcell ${c ? 'filled' : ''} ${bad ? 'conflict' : ''} ${dragging && !c ? 'droptarget' : ''}`}
                      onDragOver={canDrag ? onDragOver : undefined}
                      onDrop={canDrag ? (e) => onDrop(e, d, l) : undefined}>
                      {c && (
                        <div
                          draggable={canDrag}
                          onDragStart={canDrag ? (e) => onDragStart(e, c) : undefined}
                          className="ttb-vcontent">
                          <div className="ttb-vs">{lang === 'en' ? t('subjectNames.' + c.subject_name, c.subject_name) : c.subject_name}</div>
                          {mode !== 'class' && <div className="ttb-vc">{c.class_name}</div>}
                          {mode !== 'teacher' && <div className="ttb-vt">{c.teacher_name || ''}</div>}
                          {c.room && mode !== 'room' && <div className="ttb-vr">Otaq: {c.room}</div>}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// ---------------- Divisions (aSc-style groups per class) ----------------
function DivisionsModal({ classId, className, onClose }) {
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [newName, setNewName] = useState('');
  const [newDivision, setNewDivision] = useState('default');

  const load = async () => {
    const [gr, st] = await Promise.all([
      API.get('/timetable/groups?class_id=' + classId),
      API.get('/timetable/class-students?class_id=' + classId),
    ]);
    setItems(gr.data);
    setStudents(st.data);
  };
  useEffect(() => { load(); }, [classId]);

  const selectGroup = async (g) => {
    setSelectedGroupId(g.id);
    const r = await API.get(`/timetable/groups/${g.id}/students`);
    setSelectedStudentIds(r.data);
  };

  const toggleStudent = (sid) => {
    setSelectedStudentIds(prev =>
      prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  };

  const saveStudents = async () => {
    if (!selectedGroupId) return;
    await API.put(`/timetable/groups/${selectedGroupId}/students`, {
      student_ids: selectedStudentIds,
    });
    load();
  };

  const add = async () => {
    if (!newName.trim()) return;
    const r = await API.post('/timetable/groups', {
      class_id: classId,
      division: newDivision || 'default',
      name: newName.trim(),
      student_count: 0,
    });
    setNewName('');
    await load();
    if (r.data?.id) selectGroup({ id: r.data.id });
  };

  const remove = async (id) => {
    if (!window.confirm('Bölmə silinsin?')) return;
    await API.delete('/timetable/groups/' + id);
    if (selectedGroupId === id) { setSelectedGroupId(null); setSelectedStudentIds([]); }
    load();
  };

  // Which students are already in OTHER groups under same division label?
  const selectedGroup = items.find(g => g.id === selectedGroupId);
  const siblings = selectedGroup
    ? items.filter(g => g.division === selectedGroup.division && g.id !== selectedGroup.id)
    : [];

  const grouped = items.reduce((acc, it) => {
    const k = it.division || 'default';
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="ttb-modal-bg ttb-div-modal-bg" onClick={onClose}>
      <div className="ttb-modal asc ttb-div-modal" onClick={e => e.stopPropagation()}>
        <div className="ttb-modal-head asc">
          <span>Bölmələri təyin et — {className}</span>
          <button className="ttb-modal-x" onClick={onClose}><Icon name="close" size={16} /></button>
        </div>
        <div className="ttb-modal-body asc ttb-div-body">

          <div className="ttb-div-two-col">
            {/* LEFT: groups list */}
            <div className="ttb-div-left">
              <div className="ttb-div-info">
                Sinifi müxtəlif qruplara bölün. Sağda qrupa şagird təyin edin.
              </div>

              {Object.keys(grouped).length === 0 && (
                <div className="ttb-div-empty">Hələ bölmə yoxdur</div>
              )}

              {Object.entries(grouped).map(([div, list]) => (
                <div key={div} className="ttb-div-section">
                  <div className="ttb-div-section-title">
                    {div === 'default' ? 'Bölmələr' : div}
                  </div>
                  <div className="ttb-div-chips">
                    {list.map(g => (
                      <div key={g.id}
                        className={`ttb-div-chip ${selectedGroupId === g.id ? 'active' : ''}`}
                        onClick={() => selectGroup(g)}>
                        <span className="ttb-div-chip-name">{g.name}</span>
                        <span className="ttb-div-chip-badge">{g.student_count || 0}</span>
                        <button className="ttb-div-chip-del"
                          onClick={(e) => { e.stopPropagation(); remove(g.id); }}><Icon name="close" size={13} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="ttb-div-add">
                <input
                  type="text" placeholder="Qrup adı"
                  value={newName} onChange={e => setNewName(e.target.value)} />
                <input
                  type="text" placeholder="Etiket (cins, dil)"
                  value={newDivision === 'default' ? '' : newDivision}
                  onChange={e => setNewDivision(e.target.value || 'default')} />
                <button className="btn btn-primary" onClick={add}>+ Yarat</button>
              </div>
            </div>

            {/* RIGHT: student picker */}
            <div className="ttb-div-right">
              {!selectedGroupId ? (
                <div className="ttb-div-empty">
                  <div style={{ color: '#94a3b8' }}><Icon name="arrowLeft" size={30} /></div>
                  Soldan qrup seçin<br />
                  <small>Sonra şagirdləri təyin edə bilərsiniz</small>
                </div>
              ) : students.length === 0 ? (
                <div className="ttb-div-empty">
                  <div style={{ color: '#94a3b8' }}><Icon name="inbox" size={30} /></div>
                  Bu sinifdə şagird yoxdur<br />
                  <small>Əvvəlcə Siniflər/Qeydiyyat səhifəsindən şagird əlavə edin</small>
                </div>
              ) : (
                <>
                  <div className="ttb-div-right-head">
                    <b>{selectedGroup?.name}</b> qrupuna şagird təyin edin
                    <span className="ttb-div-count">
                      {selectedStudentIds.length} / {students.length}
                    </span>
                  </div>
                  <div className="ttb-div-students">
                    {students.map(s => {
                      const inOther = siblings.some(sg => {
                        // can't easily check without fetching; soft warning omitted
                        return false;
                      });
                      const checked = selectedStudentIds.includes(s.id);
                      return (
                        <label key={s.id} className={`ttb-div-student ${checked ? 'on' : ''}`}>
                          <input type="checkbox" checked={checked}
                            onChange={() => toggleStudent(s.id)} />
                          <span className="ttb-div-st-name">{s.name}</span>
                          {s.gender && <span className="ttb-div-st-meta">{s.gender === 'male' ? '♂' : '♀'}</span>}
                        </label>
                      );
                    })}
                  </div>
                  <div className="ttb-div-right-foot">
                    <button className="btn" onClick={() => setSelectedStudentIds([])}>Təmizlə</button>
                    <button className="btn" onClick={() => setSelectedStudentIds(students.map(s => s.id))}>Hamısını seç</button>
                    <button className="btn btn-primary" onClick={saveStudents}>Yadda saxla</button>
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
        <div className="ttb-modal-foot asc">
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn btn-primary" onClick={onClose}>Bağla</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Master Grid (aSc-style) ----------------
const SUBJECT_COLORS = [
  '#fecaca', '#fed7aa', '#fde68a', '#d9f99d', '#bbf7d0', '#a7f3d0',
  '#99f6e4', '#a5f3fc', '#bae6fd', '#bfdbfe', '#c7d2fe', '#ddd6fe',
  '#e9d5ff', '#f5d0fe', '#fbcfe8', '#fecdd3', '#fda4af', '#fdba74',
  '#fcd34d', '#86efac', '#67e8f9', '#93c5fd', '#a78bfa', '#f472b6',
];
function subjectColor(id) {
  return SUBJECT_COLORS[Math.abs((id || 0) * 7) % SUBJECT_COLORS.length];
}

function MasterGridTab({ classes, subjects, teachers, rooms, conflicts, reload }) {
  const { t, lang } = useLanguage();
  const [data, setData] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [hint, setHint] = useState('');
  const [addModal, setAddModal] = useState(null); // { class_id, day, lesson }
  const [addForm, setAddForm] = useState({ subject_id: '', teacher_id: '', room: '', length: 1, count: 1, class_type: 'whole' });

  const load = async () => {
    const r = await API.get('/timetable/full');
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const conflictIds = useMemo(() => {
    const s = new Set();
    conflicts.forEach(c => c.ids.forEach(i => s.add(i)));
    return s;
  }, [conflicts]);

  const sortedClasses = useMemo(
    () => [...classes].sort((a, b) => (a.grade_level - b.grade_level) || a.name.localeCompare(b.name)),
    [classes]);

  const cellMap = useMemo(() => {
    const m = new Map();
    for (const r of data) m.set(`${r.class_id}-${r.day_of_week}-${r.lesson_number}`, r);
    return m;
  }, [data]);

  const openAdd = (class_id, day, lesson) => {
    setAddForm({ subject_id: '', teacher_id: '', room: '', length: 1, count: 1, class_type: 'whole' });
    setAddModal({ class_id, day, lesson });
  };

  const saveAdd = async () => {
    if (!addForm.subject_id) return alert('Fənn seçin');
    const length = Math.max(1, +addForm.length || 1);
    try {
      // Place `length` consecutive lessons starting at clicked slot
      for (let k = 0; k < length; k++) {
        const ln = addModal.lesson + k;
        if (ln > 8) break;
        await API.post('/schedule', {
          class_id: addModal.class_id,
          subject_id: addForm.subject_id,
          teacher_id: addForm.teacher_id || null,
          day_of_week: addModal.day,
          lesson_number: ln,
          start_time: '08:00',
          end_time: '08:45',
          room: addForm.room || null,
          class_type: addForm.class_type || 'whole',
        });
      }
      setAddModal(null);
      load();
      reload && reload();
    } catch (e) {
      alert('Xəta: ' + (e.response?.data?.message || e.message));
    }
  };

  const onDragStart = (e, c) => { setDragging(c); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = async (e, classId, d, l) => {
    e.preventDefault();
    if (!dragging) return;
    if (dragging.class_id !== classId) {
      setHint('Yalnız eyni sinif daxilində köçürmək olar');
      setTimeout(() => setHint(''), 2500);
      setDragging(null); return;
    }
    if (dragging.day_of_week === d && dragging.lesson_number === l) { setDragging(null); return; }
    try {
      const r = await API.put('/timetable/move', { id: dragging.id, day_of_week: d, lesson_number: l });
      setHint(r.data.swapped ? 'Yerləri dəyişdirildi' : 'Köçürüldü');
      setTimeout(() => setHint(''), 2000);
      load();
    } catch (err) {
      setHint(err.response?.data?.message || 'Köçürmə alınmadı');
      setTimeout(() => setHint(''), 3000);
    } finally { setDragging(null); }
  };

  return (
    <div className="ttb-section">
      <div className="ttb-toolbar">
        <span className="ttb-hint">{t('timetable.dragHint')}</span>
        {hint && <span className="ttb-hint-msg">{hint}</span>}
        <button className="btn" style={{ marginLeft: 'auto' }} onClick={() => window.print()}><Icon name="printer" size={14} /> {t('timetable.btnPrint')}</button>
      </div>

      {data.length === 0 && (
        <div className="ttb-empty-state">
          <div style={{ color: '#94a3b8' }}><Icon name="clipboard" size={34} /></div>
          <div><b>Cədvəldə heç bir dərs yoxdur</b></div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
            Əvvəlcə <b>Tələblər</b> tabında fənləri əlavə edin,
            sonra <b>Avtomatik tərtib</b> tabında siniflər seçib <b>Avtomatik tərtib et</b> düyməsini basın.
          </div>
        </div>
      )}

      <div className="ttb-master-wrap">
        <table className="ttb-master">
          <thead>
            <tr>
              <th className="ttb-master-corner" rowSpan="2">{t('timetable.class')}</th>
              {[t('timetable.monday'), t('timetable.tuesday'), t('timetable.wednesday'), t('timetable.thursday'), t('timetable.friday')].map((d, i) => (
                <th key={i} colSpan={LESSONS.length} className="ttb-master-day">{d}</th>
              ))}
            </tr>
            <tr>
              {DAY_FULL.map((_, di) => LESSONS.map(l => (
                <th key={`${di}-${l}`} className="ttb-master-lh">{l}</th>
              )))}
            </tr>
          </thead>
          <tbody>
            {sortedClasses.map(cls => (
              <tr key={cls.id}>
                <td className="ttb-master-cls">{cls.name}</td>
                {[1, 2, 3, 4, 5].map(d => LESSONS.map(l => {
                  const c = cellMap.get(`${cls.id}-${d}-${l}`);
                  const bad = c && conflictIds.has(c.id);
                  const bg = c ? subjectColor(c.subject_id) : '';
                  return (
                    <td key={`${d}-${l}`}
                      className={`ttb-master-cell ${bad ? 'conflict' : ''} ${dragging && !c && dragging.class_id === cls.id ? 'droptarget' : ''} ${!c ? 'empty-clickable' : ''}`}
                      style={c ? { background: bg } : undefined}
                      onDragOver={onDragOver}
                      onDrop={(e) => onDrop(e, cls.id, d, l)}
                      onClick={!c ? () => openAdd(cls.id, d, l) : undefined}
                      title={c ? `${lang === 'en' ? t('subjectNames.' + c.subject_name, c.subject_name) : c.subject_name}\n${c.teacher_name || ''}\n${c.room ? t('timetable.room') + ': ' + c.room : ''}` : t('timetable.addLessonTitle')}>
                      {c && (
                        <div
                          draggable
                          onDragStart={(e) => onDragStart(e, c)}
                          className="ttb-master-content">
                          <div className="ttb-mc-subj">{((lang === 'en' ? t('subjectNames.' + c.subject_name, c.subject_name) : c.subject_name) || '').slice(0, 6)}</div>
                          {c.teacher_name && <div className="ttb-mc-tea">{c.teacher_name.split(' ').map(w => w[0]).join('').slice(0, 3)}</div>}
                        </div>
                      )}
                    </td>
                  );
                }))}
              </tr>
            ))}
            {!sortedClasses.length && (
              <tr><td colSpan={1 + 5 * LESSONS.length} className="ttb-empty">{t('timetable.noClasses')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {addModal && (
        <div className="ttb-modal-bg" onClick={() => setAddModal(null)}>
          <div className="ttb-modal asc" onClick={e => e.stopPropagation()}>
            <div className="ttb-modal-head asc">
              <span>Dərs — Yeni</span>
              <button className="ttb-modal-x" onClick={() => setAddModal(null)}><Icon name="close" size={16} /></button>
            </div>
            <div className="ttb-modal-body asc">

              <div className="asc-row">
                <div className="asc-icon"><Icon name="graduation" size={20} /></div>
                <div className="asc-field">
                  <label>Müəllim</label>
                  <select value={addForm.teacher_id} onChange={e => setAddForm({ ...addForm, teacher_id: e.target.value })}>
                    <option value="">— Seçin —</option>
                    {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="asc-row">
                <div className="asc-icon"><Icon name="book" size={20} /></div>
                <div className="asc-field">
                  <label>Fənn *</label>
                  <select value={addForm.subject_id} onChange={e => setAddForm({ ...addForm, subject_id: e.target.value })}>
                    <option value="">— Seçin —</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{lang === 'en' ? t('subjectNames.' + s.name, s.name) : s.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="asc-row">
                <div className="asc-icon"><Icon name="users" size={20} /></div>
                <div className="asc-field">
                  <label>Sinif</label>
                  <input type="text" disabled value={sortedClasses.find(c => c.id === addModal.class_id)?.name || ''} />
                </div>
                <div className="asc-field narrow">
                  <label>Tip</label>
                  <select value={addForm.class_type} onChange={e => setAddForm({ ...addForm, class_type: e.target.value })}>
                    <option value="whole">Tam sinif</option>
                    <option value="half_a">Yarım sinif (A)</option>
                    <option value="half_b">Yarım sinif (B)</option>
                  </select>
                </div>
              </div>

              <div className="asc-row">
                <div className="asc-icon"><Icon name="calendar" size={20} /></div>
                <div className="asc-field">
                  <label>Həftədə dərs</label>
                  <input type="number" min="1" max="8" value={addForm.count}
                    onChange={e => setAddForm({ ...addForm, count: +e.target.value })} />
                </div>
                <div className="asc-field narrow">
                  <label>Növ</label>
                  <select value={addForm.length} onChange={e => setAddForm({ ...addForm, length: +e.target.value })}>
                    <option value={1}>Tək</option>
                    <option value={2}>İkiqat</option>
                    <option value={3}>Üçlük</option>
                  </select>
                </div>
              </div>

              <div className="asc-row">
                <div className="asc-icon"><Icon name="door" size={20} /></div>
                <div className="asc-field">
                  <label>Otaq</label>
                  <select value={addForm.room} onChange={e => setAddForm({ ...addForm, room: e.target.value })}>
                    <option value="">— Seçin —</option>
                    {rooms.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="asc-slot-info">
                {DAY_FULL[addModal.day - 1]}, {addModal.lesson}-ci dərsdən başlayır
              </div>
            </div>

            <div className="ttb-modal-foot asc">
              <span className="asc-help">?</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={saveAdd}>OK</button>
                <button className="btn" onClick={() => setAddModal(null)}>İmtina</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
